/**
 * 费率。
 *
 * **规则不可变** —— 只插入，永不 UPDATE、永不 DELETE。改价 = 插一条带新
 * `effective_from` 的规则，历史规则原封不动留着。
 *
 * 理由很直接：一笔三个月前的交易「当时按什么费率收的」必须永远说得清，
 * 否则对账、纠纷、审计全部失去依据。所以这个页面没有「编辑」和「删除」按钮，
 * 只有「改价」——它其实是新增一条。
 *
 * 「倒填」也被禁止：`effective_from` 不能早于当前时间，那等于改历史。
 */

import { useState } from 'react';
import { request, type Collection } from '../lib/api.ts';
import { useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge, Button, Card, CopyId, ErrorBox, Field, MinorAmount, Modal, Select, TextInput,
} from '../components/ui.tsx';
import { DataTable, type Column } from '../components/table.tsx';
import { formatBps } from '../lib/money.ts';
import { formatDateTime } from '../lib/format.ts';
import type { Channel, FeeRule, Org } from '../lib/types.ts';

/** 规则当前处于什么阶段：未生效 / 生效中 / 已失效。 */
function rulePhase(rule: FeeRule): 'future' | 'active' | 'expired' {
  const now = Date.now();
  if (new Date(rule.effective_from).getTime() > now) return 'future';
  if (rule.effective_to !== null && new Date(rule.effective_to).getTime() <= now) return 'expired';
  return 'active';
}

export function FeesPage() {
  const session = useSession();
  const [orgId, setOrgId] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [creating, setCreating] = useState(false);

  const list = useAsync<Collection<FeeRule>>(
    '/v1/fees',
    { query: { org_id: orgId === '' ? null : orgId, active: onlyActive ? 'true' : null } },
    [orgId, onlyActive],
  );
  const orgs = useAsync<{ data: readonly Org[] }>('/v1/orgs', { query: { limit: 200 } });

  const orgName = (id: string): string =>
    orgs.data?.data.find((org) => org.id === id)?.name ?? id;

  // 只能给严格下级定价 —— 商户没有下级，所以商户这里只能看
  const canPrice = session.can('admin') && !session.isMerchant;

  const columns: readonly Column<FeeRule>[] = [
    { key: 'org', header: '适用组织', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{orgName(row.org_id)}</span>
        <CopyId id={row.org_id} />
      </div>
    ) },
    { key: 'scope', header: '适用范围', render: (row) => (
      <div className="row tight">
        {row.channel_account_id === null
          ? <Badge tone="warn">全通道</Badge>
          : <span className="mono" style={{ fontSize: 11.5 }}>{row.channel_account_id.slice(-6)}</span>}
        {row.currency !== null && <Badge tone="neutral">{row.currency}</Badge>}
        {row.brand !== null && <Badge tone="neutral">{row.brand}</Badge>}
        {row.method !== null && <Badge tone="neutral">{row.method}</Badge>}
      </div>
    ) },
    { key: 'rate', header: '费率', numeric: true, render: (row) => (
      <span className="num cell-strong">{formatBps(row.rate_bps)}</span>
    ) },
    // currency 为 null 表示这条规则对所有币种生效 —— 固定费的小数位因此不确定，
    // 按 USD 渲染会让 JPY 的规则显示成 1/100，所以只在币种确定时才格式化
    { key: 'fixed', header: '固定费', numeric: true, render: (row) => (
      <MinorAmount value={row.fixed_minor} currencies={row.currency === null ? [] : [row.currency]} />
    ) },
    { key: 'bounds', header: '封底 / 封顶', numeric: true, render: (row) => (
      <span className="num cell-muted">
        {row.min_fee ?? '—'} / {row.max_fee ?? '—'}
      </span>
    ) },
    { key: 'priority', header: '优先级', numeric: true, render: (row) => row.priority },
    { key: 'window', header: '生效区间', render: (row) => {
      const phase = rulePhase(row);
      return (
        <div className="stack tight">
          <div className="row tight">
            <Badge tone={phase === 'active' ? 'good' : phase === 'future' ? 'info' : 'neutral'}>
              {phase === 'active' ? '生效中' : phase === 'future' ? '未生效' : '已失效'}
            </Badge>
          </div>
          <span className="cell-muted mono" style={{ fontSize: 11 }}>
            {formatDateTime(row.effective_from)} → {row.effective_to === null ? '长期' : formatDateTime(row.effective_to)}
          </span>
        </div>
      );
    } },
  ];

  return (
    <>
      <div className="row">
        <h1>费率</h1>
        <span style={{ flex: 1 }} />
        {canPrice && <Button variant="primary" onClick={() => { setCreating(true); }}>改价</Button>}
      </div>

      <div className="alert" data-tone="info">
        <span className="alert-title">费率规则不可变</span>
        <span>
          规则只能追加，不能修改或删除。改价请新增一条并设置生效时间；历史规则必须原样保留，
          否则「这笔三个月前的交易当时按什么费率收的」就永远说不清了。
          <strong>倒填也被禁止</strong> —— 生效时间不能早于当前时刻。
        </span>
      </div>

      <div className="filters">
        <Field label="组织">
          <Select
            value={orgId}
            onChange={setOrgId}
            placeholder="全部（含子树）"
            options={(orgs.data?.data ?? []).map((org) => ({ value: org.id, label: org.name }))}
          />
        </Field>
        <Field label=" ">
          <label className="checkbox">
            <input type="checkbox" checked={onlyActive} onChange={(e) => { setOnlyActive(e.target.checked); }} />
            只看当前生效
          </label>
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.data?.data ?? []}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty="还没有配置任何费率"
          emptyHint="没有费率就没法计费 —— 建议在下发 MID 时一并配置。"
        />
      </Card>

      {creating && (
        <CreateFeeModal
          onClose={() => { setCreating(false); }}
          onDone={() => { setCreating(false); list.reload(); }}
        />
      )}
    </>
  );
}

function CreateFeeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const session = useSession();
  const [orgId, setOrgId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [rateBps, setRateBps] = useState('300');
  const [fixedMinor, setFixedMinor] = useState('30');
  const [currency, setCurrency] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');

  const orgs = useAsync<{ data: readonly Org[] }>('/v1/orgs', { query: { limit: 200 } });
  const channels = useAsync<Collection<Channel>>('/v1/channels');

  const create = useMutation(async () => await request<FeeRule>('/v1/fees', {
    method: 'POST',
    body: {
      org_id: orgId,
      ...(channelId === '' ? {} : { channel_account_id: channelId }),
      rate_bps: Number(rateBps),
      fixed_minor: fixedMinor,
      ...(currency === '' ? {} : { currency: currency.toUpperCase() }),
      ...(effectiveFrom === '' ? {} : { effective_from: new Date(effectiveFrom).toISOString() }),
    },
  }));

  // 通配规则（不指定通道）只有平台能建：代理的定价权来自按通道发的授权，
  // 一条不指定通道的规则无法对应到任何一张 grant，也就无法证明加价在授权内
  const canWildcard = session.isPlatform;

  return (
    <Modal
      title="新增费率规则"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={create.pending || orgId === '' || (!canWildcard && channelId === '')}
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {create.pending ? '提交中…' : '新增'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      <Field label="适用组织" hint="只能给严格下级定价 —— 你付的价由你的上级决定。">
        <Select
          value={orgId}
          onChange={setOrgId}
          placeholder="选择组织"
          options={(orgs.data?.data ?? [])
            .filter((org) => org.id !== session.me.org.id)
            .map((org) => ({ value: org.id, label: `${org.name}（${org.type}）` }))}
        />
      </Field>

      <Field
        label="通道账户"
        hint={
          canWildcard
            ? '留空表示对所有通道生效（通配规则）。通配规则优先级更小时会排在所有通道专用规则之前。'
            : '必选 —— 只有平台可以创建不指定通道的通配规则。你的定价权来自按通道发的授权，所以必须逐通道定价。'
        }
      >
        <Select
          value={channelId}
          onChange={setChannelId}
          {...(canWildcard ? { placeholder: '全部通道（通配）' } : { placeholder: '选择通道' })}
          options={(channels.data?.data ?? []).map((channel) => ({
            value: channel.id,
            label: `${channel.channel_code} · ${channel.label}`,
          }))}
        />
      </Field>

      <div className="grid cols-3">
        <Field label="费率（基点）" hint={formatBps(Number(rateBps) || 0)}>
          <TextInput value={rateBps} onChange={setRateBps} numeric />
        </Field>
        <Field label="固定费（最小单位）" hint="30 = USD 0.30">
          <TextInput value={fixedMinor} onChange={setFixedMinor} numeric />
        </Field>
        <Field label="币种" hint="留空 = 所有币种">
          <TextInput value={currency} onChange={setCurrency} placeholder="USD" />
        </Field>
      </div>

      <Field
        label="生效时间"
        hint="留空表示立即生效。可以排未来的价，但不能倒填 —— 倒填等于改历史。"
      >
        <TextInput value={effectiveFrom} onChange={setEffectiveFrom} type="datetime-local" />
      </Field>
    </Modal>
  );
}
